/**
 * Report Templates API Helper
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetch block data from the backend
 * @param {string} source - Data source (e.g., 'financial_statements.income_statement')
 * @param {string} planId - Business plan ID
 * @param {string} token - Auth token
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Block data
 */
export const fetchBlockData = async (source, planId, token, options = {}) => {
  const {
    detailLevel = 'standard',
    years = [2025, 2026, 2027, 2028, 2029],
  } = options;
  
  const params = new URLSearchParams({
    plan_id: planId,
    detail_level: detailLevel,
    years: years.join(','),
  });
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/block-data/${source}?${params}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  
  if (!response.ok) {
    throw new Error(`Failed to fetch block data: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Fetch chart data from the backend
 * @param {string} source - Chart source (e.g., 'revenue_projections')
 * @param {string} planId - Business plan ID
 * @param {string} token - Auth token
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Chart data
 */
export const fetchChartData = async (source, planId, token, options = {}) => {
  const {
    chartType = 'bar',
    years = 5,
  } = options;
  
  const params = new URLSearchParams({
    plan_id: planId,
    chart_type: chartType,
    years: years.toString(),
  });
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/chart-data/${source}?${params}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  
  if (!response.ok) {
    throw new Error(`Failed to fetch chart data: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Template CRUD operations
 */
export const templateApi = {
  /**
   * Create a new template
   */
  create: async (template, token) => {
    const response = await fetch(`${BACKEND_URL}/api/report-templates`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(template),
    });
    return response.json();
  },
  
  /**
   * Get a template by ID
   */
  get: async (templateId, token) => {
    const response = await fetch(
      `${BACKEND_URL}/api/report-templates/${templateId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.json();
  },
  
  /**
   * Update a template
   */
  update: async (templateId, updates, token) => {
    const response = await fetch(
      `${BACKEND_URL}/api/report-templates/${templateId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      }
    );
    return response.json();
  },
  
  /**
   * Delete a template
   */
  delete: async (templateId, token) => {
    const response = await fetch(
      `${BACKEND_URL}/api/report-templates/${templateId}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.json();
  },
  
  /**
   * List all templates
   */
  list: async (token, options = {}) => {
    const { audience, search, page = 1, perPage = 20 } = options;
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    if (audience) params.set('audience', audience);
    if (search) params.set('search', search);
    
    const response = await fetch(
      `${BACKEND_URL}/api/report-templates?${params}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.json();
  },
  
  /**
   * Clone a template
   */
  clone: async (templateId, token, newName) => {
    const response = await fetch(
      `${BACKEND_URL}/api/report-templates/${templateId}/clone`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newName }),
      }
    );
    return response.json();
  },
  
  /**
   * Get data sources
   */
  getSources: async () => {
    const response = await fetch(`${BACKEND_URL}/api/report-templates/sources`);
    return response.json();
  },
};

/**
 * Generate AI narrative for a report block
 * @param {Object} params - Narrative generation parameters
 * @param {string} params.planId - Business plan ID
 * @param {string} params.narrativeType - Type of narrative
 * @param {string} params.tone - Tone of the narrative
 * @param {string} params.length - Length of the narrative
 * @param {string} params.language - Language (en or am)
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Generated narrative
 */
export const generateNarrative = async (params, token) => {
  const {
    planId,
    narrativeType = 'executive_summary',
    tone = 'formal',
    length = 'standard',
    language = 'en',
    customContext = '',
    customRequirements = '',
  } = params;
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/generate-narrative`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        plan_id: planId,
        narrative_type: narrativeType,
        tone,
        length,
        language,
        custom_context: customContext,
        custom_requirements: customRequirements,
      }),
    }
  );
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to generate narrative: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Get available narrative options
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Narrative options
 */
export const getNarrativeOptions = async (token) => {
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/narrative-options`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  
  if (!response.ok) {
    throw new Error(`Failed to fetch narrative options: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * R2.1: Render chart server-side
 * @param {Object} chartData - Chart data in Chart.js format {labels, datasets}
 * @param {string} chartType - Chart type (bar, line, pie, etc.)
 * @param {string} title - Optional chart title
 * @param {number} width - Image width in pixels
 * @param {number} height - Image height in pixels
 * @returns {Promise<Object>} Rendered chart image data
 */
export const renderChartServerSide = async (chartData, chartType = 'bar', title = null, width = 800, height = 400) => {
  const params = new URLSearchParams({
    chart_type: chartType,
    width: width.toString(),
    height: height.toString()
  });
  
  if (title) {
    params.append('title', title);
  }
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/render-chart?${params}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(chartData)
    }
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to render chart');
  }
  
  return response.json();
};

/**
 * R2.1: Render chart for a report block
 * @param {Object} block - Report block data
 * @param {string} token - Auth token
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {Promise<Object>} Rendered chart image data
 */
export const renderChartForBlock = async (block, token, width = 800, height = 400) => {
  const params = new URLSearchParams({
    width: width.toString(),
    height: height.toString()
  });
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/render-chart-for-block?${params}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(block)
    }
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to render chart');
  }
  
  return response.json();
};

/**
 * R2.3: Pre-flight data check before report generation
 * @param {string} templateId - Template ID
 * @param {string} planId - Business plan ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Data availability check results
 */
export const preflightCheck = async (templateId, planId, token) => {
  const params = new URLSearchParams({ plan_id: planId });
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/preflight/${templateId}?${params}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Preflight check failed');
  }
  
  return response.json();
};

// ============================================
// R3.1: Data Source Registry API
// ============================================

/**
 * List all available data sources
 * @param {string} category - Optional category filter
 * @returns {Promise<Object>} List of data sources
 */
export const listDataSources = async (category = null) => {
  const params = category ? new URLSearchParams({ category }) : '';
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/data-sources${params ? '?' + params : ''}`,
    { method: 'GET' }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch data sources');
  }
  
  return response.json();
};

/**
 * Get detailed info about a data source
 * @param {string} sourceName - Data source name
 * @returns {Promise<Object>} Data source details
 */
export const getDataSourceInfo = async (sourceName) => {
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/data-sources/${sourceName}`,
    { method: 'GET' }
  );
  
  if (!response.ok) {
    throw new Error(`Data source '${sourceName}' not found`);
  }
  
  return response.json();
};

/**
 * Validate a data source configuration
 * @param {string} sourceName - Data source name
 * @param {string} chartType - Chart type
 * @param {Object} data - Optional data to validate
 * @returns {Promise<Object>} Validation result
 */
export const validateDataSourceConfig = async (sourceName, chartType, data = null) => {
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/data-sources/validate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_name: sourceName, chart_type: chartType, data })
    }
  );
  
  if (!response.ok) {
    throw new Error('Validation failed');
  }
  
  return response.json();
};

// ============================================
// R3.2: Smart Aggregation API
// ============================================

/**
 * Get aggregated revenue data
 * @param {string} planId - Business plan ID
 * @param {string} token - Auth token
 * @param {Object} options - Aggregation options
 * @returns {Promise<Object>} Aggregated revenue data
 */
export const aggregateRevenue = async (planId, token, options = {}) => {
  const params = new URLSearchParams({
    period_type: options.periodType || 'monthly',
    include_forecasts: options.includeForecasts !== false ? 'true' : 'false'
  });
  
  if (options.startDate) params.append('start_date', options.startDate);
  if (options.endDate) params.append('end_date', options.endDate);
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/aggregate/${planId}/revenue?${params}`,
    {
      headers: { 'Authorization': `Bearer ${token}` }
    }
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to aggregate revenue data');
  }
  
  return response.json();
};

/**
 * Get aggregated cost data
 * @param {string} planId - Business plan ID
 * @param {string} token - Auth token
 * @param {Object} options - Aggregation options
 * @returns {Promise<Object>} Aggregated cost data
 */
export const aggregateCosts = async (planId, token, options = {}) => {
  const params = new URLSearchParams({
    period_type: options.periodType || 'monthly'
  });
  
  if (options.startDate) params.append('start_date', options.startDate);
  if (options.endDate) params.append('end_date', options.endDate);
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/aggregate/${planId}/costs?${params}`,
    {
      headers: { 'Authorization': `Bearer ${token}` }
    }
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to aggregate cost data');
  }
  
  return response.json();
};

/**
 * Get aggregated profit data
 * @param {string} planId - Business plan ID
 * @param {string} token - Auth token
 * @param {Object} options - Aggregation options
 * @returns {Promise<Object>} Aggregated profit data with margins
 */
export const aggregateProfit = async (planId, token, options = {}) => {
  const params = new URLSearchParams({
    period_type: options.periodType || 'monthly'
  });
  
  if (options.startDate) params.append('start_date', options.startDate);
  if (options.endDate) params.append('end_date', options.endDate);
  
  const response = await fetch(
    `${BACKEND_URL}/api/report-templates/aggregate/${planId}/profit?${params}`,
    {
      headers: { 'Authorization': `Bearer ${token}` }
    }
  );
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || 'Failed to aggregate profit data');
  }
  
  return response.json();
};

export default templateApi;

